import Phaser from 'phaser';
import { COLORS, COLORS_HEX, FONTS } from '@/ui/theme';
import type { EducationalIntroConfig } from '@/types/StageConfig';
import { AudioManager } from '@/utils/AudioManager';
import { KioskWatchdog } from '@/utils/KioskWatchdog';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';
import { createImageCard } from '@/ui/ImageCard';
import { GamepadNav } from '@/utils/GamepadNav';
import { templateCardText } from '@/utils/CardText';
import { HEADER_CARD_GAP } from '@/config/constants';

/**
 * EducationIntroModal — chunky cartoon redesign (2026-04-25).
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │                  HELMEPA                             │  header
 *   │           POSEIDON CHALLENGE (small)                 │
 *   │   ╭──────────────────────────────────────────────╮   │
 *   │   │                                              │   │
 *   │   │   [hero image]      Title + body copy        │   │
 *   │   │                                              │   │
 *   │   │              [ LET'S PLAY ]                  │   │
 *   │   ╰──────────────────────────────────────────────╯   │
 *   └──────────────────────────────────────────────────────┘
 *
 * Visual language matches the redesigned MenuScene: stage background
 * stays visible behind a soft scrim, the card has a deep-navy fill
 * with thin aqua trim, and the CTA reuses the cyan rectangular menu
 * button. No bronze, no parchment, no Greek columns.
 */
export class EducationIntroModal extends Phaser.GameObjects.Container {
  private readonly overlay: Phaser.GameObjects.Rectangle;
  private readonly panel: Phaser.GameObjects.Graphics;
  private readonly headerGroup: Phaser.GameObjects.Container;
  private readonly cardGroup: Phaser.GameObjects.Container;
  private readonly ctaButton: Phaser.GameObjects.Container;
  private readonly onDismiss: () => void;
  private dismissed = false;
  private nav?: GamepadNav;
  private padTick?: () => void;

  private static readonly PANEL_W = 1180;
  private static readonly PANEL_H = 720;
  private static readonly PANEL_RADIUS = 32;

  constructor(
    scene: Phaser.Scene,
    config: EducationalIntroConfig,
    onDismiss: () => void,
  ) {
    super(scene, 0, 0);
    scene.add.existing(this);
    this.setDepth(10_000);
    this.setScrollFactor(0);
    this.onDismiss = onDismiss;

    const cx = 960;
    const pw = EducationIntroModal.PANEL_W;
    const ph = EducationIntroModal.PANEL_H;
    const panelLeft = cx - pw / 2;

    // ── soft scrim — game scene reads through but the saturated panel pops
    this.overlay = scene.add.rectangle(0, 0, 1920, 1080, 0x020b18, 0.42)
      .setOrigin(0, 0)
      .setInteractive(); // swallows clicks behind modal
    this.add(this.overlay);

    // ── header group: HELMEPA + smaller POSEIDON CHALLENGE banner ─────
    this.headerGroup = scene.add.container(0, 0);
    this.add(this.headerGroup);
    if (scene.textures.exists('helmepa_logo')) {
      const helmepa = scene.add.image(cx, 88, 'helmepa_logo').setOrigin(0.5);
      helmepa.setScale(162 / helmepa.width);
      helmepa.setAlpha(0.95);
      this.headerGroup.add(helmepa);
    }
    let titleBottom = 268.6; // fallback if title texture is missing
    if (scene.textures.exists('title_logo_v4')) {
      const title = scene.add.image(cx, 200, 'title_logo_v4').setOrigin(0.5);
      title.setScale(378 / title.width);
      this.headerGroup.add(title);
      titleBottom = title.y + title.displayHeight / 2;
    }

    // ── main card panel ────────────────────────────────────────────────
    const panelTop = Math.round(titleBottom + HEADER_CARD_GAP);
    this.panel = scene.add.graphics();
    this.drawPanel(this.panel, panelLeft, panelTop, pw, ph, EducationIntroModal.PANEL_RADIUS);
    this.add(this.panel);

    // ── card content. When an image is provided, a 9:16 hero sits on the
    //    left with a centered copy column to its right. When `image` is an
    //    empty string the card is text-only and the copy is centered across
    //    the whole panel (e.g. the "Cigarette butts are not paper" tip).
    this.cardGroup = scene.add.container(0, 0);
    this.add(this.cardGroup);

    const imgInsetTop = 40;
    const imgInsetX = 40;
    const ctaHalfH = 55;
    const ctaY = panelTop + ph - 95;
    const copyPadX = 70;
    const copyGap = 60;

    const wantsImage = !!config.image;
    const textureKey = wantsImage ? this.getIntroTextureKey(config.image) : null;
    const hasImage = wantsImage && !!textureKey && scene.textures.exists(textureKey);

    // Resolve the copy column's horizontal centre + wrap width per mode.
    let textCenterX: number;
    let textW: number;
    if (wantsImage) {
      const imgY = panelTop + imgInsetTop;
      const imgH = (ctaY + ctaHalfH) - imgY;
      const imgX = panelLeft + imgInsetX;
      // Size the frame to the image's own aspect ratio so the whole
      // illustration fills it edge to edge, with no crop and no letterbox bands.
      let imgW = Math.round(imgH * 9 / 16);
      if (hasImage) {
        const src = scene.textures.get(textureKey as string).getSourceImage() as HTMLImageElement;
        imgW = Math.round(imgH * src.width / src.height);
        this.cardGroup.add(createImageCard(scene, {
          x: imgX, y: imgY, w: imgW, h: imgH,
          textureKey: textureKey as string,
        }));
      } else {
        const placeholder = scene.add.text(
          imgX + imgW / 2,
          imgY + imgH / 2,
          `[missing:\n${config.image}]`,
          { fontFamily: FONTS.display, fontSize: '24px', color: COLORS_HEX.textDanger, align: 'center' },
        ).setOrigin(0.5);
        this.cardGroup.add(placeholder);
      }
      const colLeft = imgX + imgW + copyGap;
      const colRight = panelLeft + pw - copyPadX;
      textCenterX = (colLeft + colRight) / 2;
      textW = colRight - colLeft;
    } else {
      textCenterX = cx;
      textW = pw - copyPadX * 2;
    }

    // ── title block — small light "MISSION N" eyebrow above a big yellow
    //    Skranji headline. Split on the first " - " or ":" so titles like
    //    "MISSION 2 - THE SHORE" or "MISSION 2: THE SHORE" both become kicker
    //    + hero line; a separator-less title is headline only.
    const rawTitle = config.title ? templateCardText(scene, config.title) : '';
    const dashIdx = rawTitle.indexOf(' - ');
    const colonIdx = rawTitle.indexOf(':');
    let sepIdx = -1;
    let sepLen = 0;
    if (dashIdx !== -1) { sepIdx = dashIdx; sepLen = 3; }
    else if (colonIdx !== -1) { sepIdx = colonIdx; sepLen = 1; }
    const eyebrowStr = sepIdx !== -1 ? rawTitle.slice(0, sepIdx).trim() : '';
    const headlineStr = sepIdx !== -1 ? rawTitle.slice(sepIdx + sepLen).trim() : rawTitle;

    const eyebrowText = eyebrowStr
      ? scene.add.text(textCenterX, 0, eyebrowStr.toUpperCase(), {
          fontFamily: FONTS.display,
          fontSize: '26px',
          color: COLORS_HEX.textSecondary,
          letterSpacing: 6,
          align: 'center',
        }).setOrigin(0.5, 0)
      : null;
    if (eyebrowText) this.cardGroup.add(eyebrowText);

    const headlineText = headlineStr
      ? scene.add.text(textCenterX, 0, headlineStr, {
          fontFamily: FONTS.fancyRegular,
          fontSize: hasImage ? '60px' : '54px',
          color: '#ffe000',
          stroke: '#000000',
          strokeThickness: 7,
          wordWrap: { width: textW, useAdvancedWrap: true },
          lineSpacing: 2,
          align: 'center',
          shadow: { offsetX: 0, offsetY: 4, color: '#000000', blur: 10, fill: true },
        }).setOrigin(0.5, 0)
      : null;
    if (headlineText) this.cardGroup.add(headlineText);

    const bodyText = scene.add.text(textCenterX, 0, templateCardText(scene, config.body), {
      fontFamily: FONTS.displayLight,
      fontSize: '26px',
      color: COLORS_HEX.textPrimary,
      shadow: { offsetX: 0, offsetY: 2, color: COLORS_HEX.midnightDepth, blur: 4, fill: true },
      wordWrap: { width: textW, useAdvancedWrap: true },
      lineSpacing: 8,
      align: 'center',
    }).setOrigin(0.5, 0);
    this.cardGroup.add(bodyText);

    // ── CTA — same cyan rectangular button as PLAY on the menu, centered
    //    under the copy column.
    this.ctaButton = createPillButton(scene, {
      x: textCenterX,
      y: ctaY,
      label: config.cta,
      variant: 'primary',
      fontSize: 34,
      onClick: () => this.dismiss(),
    });
    this.add(this.ctaButton);

    // ── vertical stack: eyebrow → headline → body, as one block centred in
    //    the band between the panel top and the CTA, clear of the button.
    const stackTop = panelTop + 60;
    const stackBottom = ctaY - 70;
    const eyebrowH = eyebrowText ? eyebrowText.height + 12 : 0;
    const headlineH = headlineText ? headlineText.height + 44 : 0;
    const blockH = eyebrowH + headlineH + bodyText.height;
    let cursorY = stackTop + Math.max(0, (stackBottom - stackTop - blockH) / 2);
    if (eyebrowText) { eyebrowText.y = cursorY; cursorY += eyebrowH; }
    if (headlineText) { headlineText.y = cursorY; cursorY += headlineH; }
    bodyText.y = cursorY;

    this.setAlpha(0);
    (this.panel as any).setScale?.(0.96);

    // Gamepad: any face button or Start dismisses the card. Lives on the
    // modal (not the scene) so the listener tears down with the dismiss tween.
    this.nav = new GamepadNav(scene, {
      onConfirm: () => this.dismiss(),
      onCancel:  () => this.dismiss(),
      onPause:   () => this.dismiss(),
    });

    // Selection halo on the lone CTA — visible whenever a gamepad is
    // connected, since any face button dismisses and the joystick is
    // effectively "on" the only target. Hidden for mouse / keyboard.
    const haloGraphics = scene.add.graphics();
    this.add(haloGraphics);
    this.bringToTop(this.ctaButton);
    this.padTick = () => {
      this.nav?.update();
      if (this.nav?.isPadConnected()) {
        drawSelectionHalo(haloGraphics, {
          cx: this.ctaButton.x,
          cy: this.ctaButton.y,
          w: ((this.ctaButton as any).pillWidth ?? 0) * this.ctaButton.scaleX,
          h: ((this.ctaButton as any).pillHeight ?? 0) * this.ctaButton.scaleY,
          rx: ((this.ctaButton as any).pillCapRx ?? 0) * this.ctaButton.scaleX,
          ry: ((this.ctaButton as any).pillCapRy ?? 0) * this.ctaButton.scaleY,
        }, { gap: 14 });
      } else {
        haloGraphics.clear();
      }
    };
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.padTick);
  }

  show(): void {
    // Mark a blocking dialog open for the kiosk idle watchdog; release it when
    // this object is destroyed (covers both normal dismiss and scene shutdown,
    // exactly once, so the counter can't leak).
    KioskWatchdog.instance.pushModal();
    this.once(Phaser.GameObjects.Events.DESTROY, () => KioskWatchdog.instance.popModal());
    this.scene.tweens.add({ targets: this, alpha: 1, duration: 280, ease: 'Power2' });
    this.scene.tweens.add({
      targets: [this.panel],
      scaleX: 1, scaleY: 1,
      duration: 360, ease: 'Back.easeOut',
    });
    AudioManager.getInstance().playSFX(this.scene, 'sfx_whoosh_reveal', { volume: 0.45 });
  }

  // ── visual helpers ────────────────────────────────────────────────────
  private drawPanel(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, r: number): void {
    // Matches StageClearScene / GameOverScene: translucent navy fill
    // + single aqua hairline. One modal chrome across the whole game.
    g.fillStyle(COLORS.deepOcean, 0.55);
    g.fillRoundedRect(x, y, w, h, r);
    g.lineStyle(2, COLORS.aqua, 0.70);
    g.strokeRoundedRect(x, y, w, h, r);
  }

  private dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    if (this.padTick) {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.padTick);
      this.padTick = undefined;
    }
    this.nav?.destroy();
    this.nav = undefined;
    AudioManager.getInstance().playSFX(this.scene, 'sfx_iris_open', { volume: 0.5 });
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: 220,
      ease: 'Power2',
      onComplete: () => {
        this.onDismiss();
        this.destroy();
      },
    });
  }

  private getIntroTextureKey(assetPath: string): string | null {
    if (!assetPath) return null;
    const filename = assetPath.split('/').pop() ?? '';
    return filename.replace(/\.(png|jpg|jpeg|webp)$/i, '');
  }
}
